import type { ResourceBundle, UpgradeRequirements } from '@/types/EntityTypes';
import { Entity } from './Entity';
import type { GameState } from '@/types/GameState';

export interface PurchaseCheck {
  allowed: boolean;
  reason?: string;
}

export function checkUpgradePurchase(upg: UpgradeEntity, state: GameState): PurchaseCheck {
  if (upg.isPurchased(state)) {
    return { allowed: false, reason: 'Upgrade already purchased.' };
  }

  // Check if this is a research upgrade (requires researchProgress in resources)
  const isResearchUpgrade = upg.progressionRequirements?.resources?.researchProgress !== undefined;

  if (isResearchUpgrade) {
    // Research upgrades cost research points, not money
    const requiredResearch = upg.progressionRequirements?.resources?.researchProgress || 0;
    if ((state.resources.researchProgress || 0) < requiredResearch) {
      return { allowed: false, reason: `Not enough research points. Need ${requiredResearch}, have ${state.resources.researchProgress || 0}` };
    }
  } else {
    // Regular upgrades cost money
    if (!upg.canAfford((state.resources.money || 0))) {
      return { allowed: false, reason: 'Not enough money.' };
    }
  }

  if (!upg.progressionRequirements) {
    return { allowed: true };
  }
  const { upgrades, personnel, infrastructure, resources } = upg.progressionRequirements;

  if (upgrades) {
    for (const requiredUpgrade of upgrades) {
      if (!state.upgrades[requiredUpgrade]) {
        return { allowed: false, reason: `Requires upgrade: ${requiredUpgrade}` };
      }
    }
  }
  if (personnel) {
    for (const [role, required] of Object.entries(personnel)) {
      const current = (state.personnel as any)[role] || 0;
      if (current < required) {
        return { allowed: false, reason: `Requires ${required} ${role}` };
      }
    }
  }
  if (infrastructure) {
    for (const [infra, required] of Object.entries(infrastructure)) {
      const current = (state.infrastructure as any)[infra] || 0;
      if (current < required) {
        return { allowed: false, reason: `Requires ${required} ${infra}` };
      }
    }
  }
  if (resources) {
    for (const [dataType, required] of Object.entries(resources)) {
      // Skip researchProgress check as it's handled above
      if (dataType === 'researchProgress') continue;

      const current = (state.resources as any)[dataType] || 0;
      if (current < required) {
        return { allowed: false, reason: `Requires ${required} ${dataType}` };
      }
    }
  }
  return { allowed: true };
}

export class UpgradeEntity extends Entity {
  public effect?: (state: GameState) => void;
  public upgradeStage?: string;

  constructor(params: {
    id: string;
    name: string;
    description: string;
    cost: number;
    storage?: ResourceBundle;
    consumes?: ResourceBundle;
    produces?: ResourceBundle;
    progressionRequirements?: UpgradeRequirements;
    
    effect?: (state: GameState) => void;
    upgradeStage?: string;
  }) {
    super(params);

    this.effect = params.effect;
    this.upgradeStage = params.upgradeStage;
  }

  canAfford(money: number): boolean {
    return money >= this.cost;
  }

  isPurchased(gameState: { upgrades: Record<string, boolean> }): boolean {
    return gameState.upgrades[this.id] || false;
  }

  canBePurchased(gameState: any): boolean {
    return checkUpgradePurchase(this, gameState).allowed;
  }

  meetsRequirements(gameState: any): boolean {
    if (!this.progressionRequirements) {
      return true;
    }
    const { upgrades, personnel, infrastructure, resources } = this.progressionRequirements;

    if (upgrades) {
      for (const requiredUpgrade of upgrades) {
        if (!gameState.upgrades[requiredUpgrade]) {
          return false;
        }
      }
    }
    if (personnel) {
      for (const [role, required] of Object.entries(personnel)) {
        const current = (gameState.personnel as any)[role] || 0;
        if (current < required) {
          return false;
        }
      }
    }
    if (infrastructure) {
      for (const [infra, required] of Object.entries(infrastructure)) {
        const current = (gameState.infrastructure as any)[infra] || 0;
        if (current < required) {
          return false;
        }
      }
    }
    if (resources) {
      for (const [dataType, required] of Object.entries(resources)) {
        // Skip researchProgress as it's handled separately for display
        if (dataType === 'researchProgress') continue;

        const current = (gameState.resources as any)[dataType] || 0;
        if (current < required) {
          return false;
        }
      }
    }
    return true;
  }

  meetsNonPersonnelRequirements(gameState: any): boolean {
    if (!this.progressionRequirements) {
      return true;
    }
    const { upgrades, infrastructure, resources } = this.progressionRequirements;

    if (upgrades) {
      for (const requiredUpgrade of upgrades) {
        if (!gameState.upgrades[requiredUpgrade]) {
          return false;
        }
      }
    }
    if (infrastructure) {
      for (const [infra, required] of Object.entries(infrastructure)) {
        const current = (gameState.infrastructure as any)[infra] || 0;
        if (current < required) {
          return false;
        }
      }
    }
    if (resources) {
      for (const [dataType, required] of Object.entries(resources)) {
        // Skip researchProgress as it's handled separately for display
        if (dataType === 'researchProgress') continue;

        const current = (gameState.resources as any)[dataType] || 0;
        if (current < required) {
          return false;
        }
      }
    }
    return true;
  }

  getRequirementsText(): string {
    if (!this.progressionRequirements) {
      return '';
    }

    const requirements: string[] = [];
    const { upgrades, personnel, infrastructure, resources } = this.progressionRequirements;

    if (upgrades && upgrades.length > 0) {
      requirements.push(`Required upgrades: ${upgrades.join(', ')}`);
    }

    if (personnel) {
      const personnelReqs = Object.entries(personnel)
        .map(([role, count]) => `${count} ${role}`)
        .join(', ');
      if (personnelReqs) {
        requirements.push(`Required personnel: ${personnelReqs}`);
      }
    }

    if (infrastructure) {
      const infraReqs = Object.entries(infrastructure)
        .map(([infra, count]) => `${count} ${infra}`)
        .join(', ');
      if (infraReqs) {
        requirements.push(`Required infrastructure: ${infraReqs}`);
      }
    }

    if (resources) {
      const dataReqs = Object.entries(resources)
        .map(([dataType, count]) => `${count} ${dataType}`)
        .join(', ');
      if (dataReqs) {
        requirements.push(`Required data: ${dataReqs}`);
      }
    }

    return requirements.join('\n');
  }

  getMissingRequirements(gameState: GameState): string[] {
    const missing: string[] = [];

    if (!this.progressionRequirements) {
      return missing;
    }

    const { upgrades, personnel, infrastructure, resources } = this.progressionRequirements;

    // Check upgrade requirements
    if (upgrades) {
      for (const requiredUpgrade of upgrades) {
        if (!gameState.upgrades[requiredUpgrade]) {
          missing.push(`Missing upgrade: ${requiredUpgrade}`);
        }
      }
    }

    // Check personnel requirements
    if (personnel) {
      for (const [role, required] of Object.entries(personnel)) {
        const current = (gameState.personnel as any)[role] || 0;
        if (current < required) {
          missing.push(`Need ${required - current} more ${role} (have ${current}/${required})`);
        }
      }
    }

    // Check infrastructure requirements
    if (infrastructure) {
      for (const [infra, required] of Object.entries(infrastructure)) {
        const current = (gameState.infrastructure as any)[infra] || 0;
        if (current < required) {
          missing.push(`Need ${required - current} more ${infra} (have ${current}/${required})`);
        }
      }
    }

    // Check resource requirements (excluding money and researchProgress which are shown elsewhere)
    if (resources) {
      for (const [dataType, required] of Object.entries(resources)) {
        if (dataType === 'researchProgress') continue;

        const current = (gameState.resources as any)[dataType] || 0;
        if (current < required) {
          missing.push(`Need ${required - current} more ${dataType} (have ${current}/${required})`);
        }
      }
    }

    return missing;
  }

  applyEffect(state: any): void {
    if (this.effect && typeof this.effect === 'function') {
      this.effect(state);
    }
  }

  getDisplayText(): string {
    // Check if this is a research upgrade
    const isResearchUpgrade = this.progressionRequirements?.resources?.researchProgress !== undefined;
    if (isResearchUpgrade) {
      const researchCost = this.progressionRequirements?.resources?.researchProgress || 0;
      return `${this.name} (${researchCost} Research)`;
    }
    return `${this.name} ($${this.cost})`;
  }

  static getAvailableUpgrades(upgrades: UpgradeEntity[], gameState: { upgrades: Record<string, boolean> }): UpgradeEntity[] {
    return upgrades.filter(upgrade =>
      !upgrade.isPurchased(gameState) && upgrade.meetsNonPersonnelRequirements(gameState)
    );
  }
} 